/**
 * One attempt at a graded assessment, as shown inside an analytics tooltip.
 *
 * Shared by ExitTicketPopover (exit ticket / pre-assessment) and
 * CustomTicketPopover (custom pages) so both read identically: optional
 * "Best Attempt" badge, an ordinal + mastery header, the correct/total count,
 * and a link into the detailed results.
 *
 * Callers own grouping and ordering; this component renders exactly what it is
 * handed. `showHeader` is false for a lone attempt, where the ordinal and
 * mastery label add nothing.
 */
import React from "react";

const ordinal = (n: number): string => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

const statusColor: Record<string, string> = {
  mastered: "text-[#1C9C6D]",
  partially_mastered: "text-[#FFA100]",
  not_mastered: "text-[#FF6A6A]",
  in_progress: "text-[#777777]",
};

/** A null score means the attempt was never submitted, so it is still running. */
const attemptStatusLabel = (
  score: number | null,
): { label: string; color: string } => {
  if (score === null)
    return { label: "In Progress", color: statusColor.in_progress ?? "" };
  if (score === 100) return { label: "Mastered", color: statusColor.mastered };
  if (score > 50)
    return {
      label: "Partially Mastered",
      color: statusColor.partially_mastered,
    };
  return { label: "Not Mastered", color: statusColor.not_mastered };
};

/**
 * The exported asset wrapped the path in a clipPath with a hardcoded id. Every
 * card renders its own copy and analytics tooltips stay mounted, so the ids
 * collided; once whichever copy owned the id unmounted, the rest pointed at a
 * missing clipPath and browsers dropped the star entirely. The clip rect was
 * the full 13x13 viewBox and clipped nothing, so it is gone.
 */
const StarIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 13 13"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M12.9661 4.9871C12.881 4.72386 12.6475 4.5369 12.3713 4.512L8.61915 4.1713L7.13544 0.69855C7.02604 0.444043 6.77689 0.279297 6.50007 0.279297C6.22324 0.279297 5.97409 0.444043 5.86469 0.699146L4.38099 4.1713L0.628234 4.512C0.352501 4.53749 0.119615 4.72386 0.0340187 4.9871C-0.0515777 5.25033 0.0274725 5.53906 0.236058 5.72106L3.07224 8.20842L2.23592 11.8924C2.17472 12.1633 2.27986 12.4433 2.50461 12.6058C2.62542 12.6931 2.76676 12.7375 2.90928 12.7375C3.03217 12.7375 3.15407 12.7044 3.26347 12.6389L6.50007 10.7045L9.73547 12.6389C9.97223 12.7813 10.2707 12.7683 10.4949 12.6058C10.7198 12.4428 10.8248 12.1627 10.7636 11.8924L9.92729 8.20842L12.7635 5.72156C12.9721 5.53906 13.0517 5.25083 12.9661 4.9871Z"
      fill="#FFC107"
    />
  </svg>
);

interface AssessmentAttemptCardProps {
  attemptId: number;
  attemptNumber: number;
  score: number | null;
  totalQuestions: number;
  correctCount: number;
  isBest?: boolean;
  showHeader: boolean;
  /**
   * Mastery wording only applies to graded assessments. Custom pages are
   * surveys, reflections and questionnaires, where "Not Mastered" misreads a
   * low score as a failed objective, so they render the header without it.
   */
  showMastery?: boolean;
  onViewDetails?: (attemptId: number) => void;
}

const AssessmentAttemptCard: React.FC<AssessmentAttemptCardProps> = ({
  attemptId,
  attemptNumber,
  score,
  totalQuestions,
  correctCount,
  isBest = false,
  showHeader,
  showMastery = true,
  onViewDetails,
}) => {
  const { label, color } = attemptStatusLabel(score);
  const bestBadge = (
    <span className="shrink-0 inline-flex items-center gap-[5px] font-semibold text-[#252525] et-analytics-caption text-[10px] leading-none bg-white border border-[#EBEBEB] rounded-[5px] p-[5px] w-fit">
      <StarIcon />
      Best Attempt
    </span>
  );
  const attemptLabel = `${ordinal(attemptNumber)} Attempt`;

  return (
    <div className="et-assessment-attempt rounded-[10px] mb-[10px] py-[10px] px-[15px] flex flex-col gap-[10px] bg-white shadow-[0_0_5px_rgba(12,113,78,0.2)]">
      {/* A graded assessment already spends the right of the score line on the
          mastery label, so its attempt number keeps the header row above it.
          Custom pages have no mastery label, and that row would sit empty on
          the left — there the number rides on the score line instead. */}
      {showMastery && showHeader && (
        <>
          {isBest && bestBadge}
          <div className="flex justify-between items-center gap-3">
            <span className="font-bold text-base text-[#252525]">
              {attemptLabel}
            </span>
            <span className={`font-bold text-base ${color}`}>{label}</span>
          </div>
        </>
      )}
      <div className="flex items-baseline justify-between gap-3">
        <span className="font-bold text-xl text-[#252525]">
          {correctCount} / {totalQuestions} correct
        </span>
        {!showMastery && showHeader && (
          <span className="shrink-0 font-semibold text-xs text-[#777777]">
            {attemptLabel}
          </span>
        )}
      </div>
      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          disabled={!onViewDetails}
          className="text-[#1C9C6D] hover:text-[#1C9C6D] font-semibold underline cursor-pointer text-sm"
          onClick={(e) => {
            e.stopPropagation();
            onViewDetails?.(attemptId);
          }}
        >
          See Detailed Results
        </button>
        {!showMastery && isBest && showHeader && bestBadge}
      </div>
    </div>
  );
};

export default AssessmentAttemptCard;
